import { modalActions } from '@/actions';
import { TERMS_URLS } from '@/constants/terms';
import { Button } from '@/components/elements/forms/buttons';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import { ListItem } from '@/components/elements/lists';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { LoginContextType } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { useEffect, useRef, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

const translationKey = `RequestToken`;

type RequestTokenProps = Omit<
  LoginContextType,
  'handleLogin' | 'updateView' | 'view' | 'user' | 'flow' | 'sessionId'
> & {
  redirectUrl: string;
  autoFocus?: boolean;
  // The "cancel with code" link navigates this window — hidden where that is a dead end (login popup).
  showCancelLink?: boolean;
};

const RequestToken = ({
  email,
  updateEmail,
  handleEmailSubmit,
  redirectUrl,
  onClose,
  autoFocus = false,
  showCancelLink = true,
}: RequestTokenProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  const inputRef = useRef<HTMLInputElement>(null);

  const query = new URLSearchParams(location.search);
  const giftcardToken = query.get('giftcardToken');

  const handleLoginClick = () => {
    handleEmailSubmit({ redirectUrl });
  };

  const handleOnBlur = () => {
    if (email.value) return;
    setShowTerms(false);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateEmail(e.target.value);
    if (e.target.value) {
      setShowTerms(true);
    }
  };

  const handleOnCancelBookingClick = () => {
    loginTrackingSession.track({ step: 'booking_cancellation', flow: 'login' });
    dispatch(modalActions.login({ show: false }));
    onClose();
  };

  const handleTermsClick = () => {
    loginTrackingSession.track({ step: 'terms_and_conditions', flow: 'login' });
  };

  useEffect(() => {
    /**
     * dont change focus on mobile or tablet devices
     * to avoid keyboard popping up unexpectedly
     */
    if (inputRef.current && !isMobile && !isTablet && autoFocus) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      <div className="gap-md flex flex-col">
        {Boolean(giftcardToken) && (
          <div className="pb-md">
            <Alert
              leftSlot={<Icon size="sm" variant="info-circle" />}
              variant="information"
              verticalAlign="top"
              rightSlot={<p>{_s(`${translationKey}.savedGiftcard`)}</p>}
            />
          </div>
        )}

        <ListInput
          ref={inputRef}
          id="email"
          type="email"
          autoComplete="email"
          leftIcon={<Icon variant="email" />}
          leftPadding={false}
          rightPadding={false}
          label={_s(`${translationKey}.input.title`)}
          placeholder={_s(`${translationKey}.input.placeholder`)}
          inputValue={email.value}
          value={email.value}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          setValue={() => updateEmail('')}
          error={email.error && email.submitting ? _s(`${translationKey}.input.error`) : undefined}
          data-cy="login-email-input"
          floatStyle={true}
          maxLength={254}
        />
        <Button block variant="primary" size="md" onClick={handleLoginClick} data-cy="login-submit-button">
          {_s(`${translationKey}.cta`)}
        </Button>
        {showTerms && (
          <p className="text-fg_secondary text-m text-center">
            {_s(`${translationKey}.terms.text`)}{' '}
            <a
              onClick={handleTermsClick}
              href={TERMS_URLS.USAGE}
              target="_blank"
              rel="noopener noreferrer"
              className="text-nowrap underline">
              {_s(`${translationKey}.terms.link`)}
            </a>
          </p>
        )}
        {!showTerms && showCancelLink && (
          <ListItem.Base to="/cancel" onClick={handleOnCancelBookingClick}>
            <div className="gap-xs flex items-center">
              <Icon variant="calendar-cross" />
              <span>{_s(`${translationKey}.cancelWithCode`)}</span>
            </div>
          </ListItem.Base>
        )}
      </div>
    </>
  );
};

export default RequestToken;
